import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { GEOJSON_PROPERTY } from './constants';
import type { AddFeatureParams, Feature, MapLayer, Props, UseFeatures } from './types';

const useFeatures = ({
    onFeatureAdded,
    onFeatureRemoved,
    onFeatureUpdated
}: Props): UseFeatures => {
    const [featureMap, setFeatureMap] = useState<Record<string, Feature>>({});

    const addFeature = useCallback(
        ({ feature, layerName }: AddFeatureParams): Feature => {
            feature.properties = {
                ...feature.properties,
                [GEOJSON_PROPERTY.ID]: feature.id ?? uuidv4(),
                [GEOJSON_PROPERTY.LAYER]: layerName
            };

            const featureId = feature.properties?.[GEOJSON_PROPERTY.ID];

            setFeatureMap(prev => ({
                ...prev,
                [featureId]: feature
            }));

            onFeatureAdded?.(feature);

            return feature;
        },
        [onFeatureAdded]
    );

    const removeFeature = useCallback(
        (featureId?: string) => {
            if (!featureId) {
                return;
            }

            setFeatureMap(prev => {
                const updated = { ...prev };

                delete updated[featureId];

                return updated;
            });

            onFeatureRemoved?.(featureId);
        },
        [onFeatureRemoved]
    );

    const updateFeature = useCallback(
        (feature?: Feature, newLayer?: MapLayer) => {
            if (!feature) {
                console.warn('Attempting to update undefined feature');

                return;
            }

            feature.properties = {
                ...feature.properties,
                [GEOJSON_PROPERTY.ID]: feature.id ?? uuidv4(),
                [GEOJSON_PROPERTY.LAYER]: newLayer ?? feature.properties?.[GEOJSON_PROPERTY.LAYER]
            };

            setFeatureMap(prev => ({
                ...prev,
                [feature.id as string]: feature
            }));

            onFeatureUpdated?.(feature);
        },
        [onFeatureUpdated]
    );

    const clear = useCallback(() => {
        setFeatureMap({});
    }, []);

    const clearByLayer = useCallback((layerName: MapLayer) => {
        setFeatureMap(prev => {
            const updated: Record<string, Feature> = {};

            for (const [featureId, feature] of Object.entries(prev)) {
                if (feature.properties?.[GEOJSON_PROPERTY.LAYER] !== layerName) {
                    updated[featureId] = feature;
                }
            }

            return updated;
        });
    }, []);

    const getFeatureById = (featureId: string) => featureMap[featureId];

    return {
        addFeature,
        clear,
        clearByLayer,
        features: Object.values(featureMap),
        getFeatureById,
        removeFeature,
        updateFeature
    };
};

export default useFeatures;
